import "@husky-hrc/Framework/Store";
import { huskyThemedButtonProps } from "@husky-hrc/Utilities/Mixins/buttons";
import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

// Load all custom widgets so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const widgets = require.context("./Widgets", true, /\.tsx$/);
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Framework/Handlers", true);
handlers.keys().forEach(key => handlers(key));

// Load all the extensions to the base widgets. This includes style extensions.
// const widgetExtensions = require.context("./WidgetExtensions", true);
// widgetExtensions.keys().forEach(key => widgetExtensions(key));

// add some pre styleguide customizations. These can be overridden by the style guid.
setPreStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 1025, 1200],
        maxWidths: [540, 540, 720, 960, 100000], // NOTE: Max width set to 100k to allow for AB&C's full width design. --Sean
    },
    focus: {
        color: "#74a4f8",
    },
    globalStyle: {
        css: css`
            #onetrust-policy-text a {
                color: ${getColor("common.accentContrast")} !important;
            }
        `,
    },
    typography: {
        // Additional font import in \Overrides\LayoutSections\HeadEnd.tsx
        fontFamilyImportUrl: "https://fonts.googleapis.com/css?family=Lato:300,400,500,600,700,800,900&display=swap",
        h1: {
            fontFamily: "'IndustryInc', 'Lato', 'Open Sans', 'Roboto', sans-serif",
            weight: 500,
            transform: "uppercase",
            css: css`
                letter-spacing: 1.31px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 40px;
                                line-height: 44px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 40px;
                                line-height: 44px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        h2: {
            fontFamily: "'Roboto', 'Lato', 'Open Sans', sans-serif",
            weight: 800,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 26px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 26px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        h3: {
            fontFamily: "'Roboto', 'Lato', 'Open Sans', sans-serif",
            weight: 700,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 20px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 20px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        h4: {
            fontFamily: "'IndustryInc', 'Roboto', 'Lato', 'Open Sans', sans-serif",
            weight: 200,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 22px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        h5: {
            fontFamily: "'IndustryInc', 'Roboto', 'Lato', 'Open Sans', sans-serif",
            weight: 500,
            color: "common.backgroundContrast",
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 16px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        h6: {
            size: "16px",
            fontFamily: "'Roboto', 'Lato', 'Open Sans', sans-serif",
            weight: 900,
        },
        body: {
            fontFamily: "'Roboto', 'Lato','Open Sans', sans-serif",
            weight: 400,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 13px;
                                line-height: 22px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 18px;
                                line-height: 26px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        p: {
            fontFamily: "'Roboto', 'Lato','Open Sans', sans-serif",
            weight: 400,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                                line-height: 22px;
                            `,
                            null,
                            null,
                            css`
                                font-size: 18px;
                                line-height: 26px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        headerPrimary: {
            forwardAs: "p",
            size: "24px",
            fontFamily: "'IndustryInc', 'Roboto', 'Lato','Open Sans', sans-serif",
            color: "text.main",
            transform: "uppercase",
        },
        headerSecondary: {
            forwardAs: "p",
            size: "15px",
            fontFamily: "'Roboto', 'Lato','Open Sans', sans-serif",
            transform: "uppercase",
            weight: 800,
        },
        headerTertiary: {
            forwardAs: "p",
            size: "15px",
            fontFamily: "'Roboto', 'Lato', 'Open Sans', sans-serif",
            color: "text.main",
            transform: "uppercase",
        },
        legend: {
            forwardAs: "span",
            size: "10px",
            color: "text.accent",
            transform: "uppercase",
            weight: 600,
        },
    },
    button: {
        primary: huskyThemedButtonProps(),
        secondary: huskyThemedButtonProps("secondary"),
        defaultProps: huskyThemedButtonProps(),
    },
    lists: {
        unorderedListProps: {
            css: css`
                li {
                    font-size: 1rem;
                    line-height: 1;
                    &::marker {
                        font-size: 1.25rem;
                    }
                }
            `,
        },
    },
});

// add some post styleguide customizations. These cannot be overridden by the style guid.
setPostStyleGuideTheme({
    colors: {
        primary: {
            main: "#FFC72C",
            contrast: "#d19a00",
        },
        secondary: {
            main: "#53565A",
        },
        danger: {
            main: "#DE2C0F",
            contrast: "#FFFFFF",
        },
        common: {
            accent: "#F6F5FB",
            accentContrast: "#DE1F07",
            border: "#95989C",
            disabled: "#F2F5F7",
            backgroundContrast: "#696f72",
        },
        text: {
            main: "#000000",
            disabled: "#53565A",
        },
    },
});
